import React, { useEffect, useState, useCallback } from 'react';
import * as identity from '../identity/identityContract';
import './UsersSettings.css';

/**
 * Users — tenant user-management console (full Team-console parity), built on the
 * engine-neutral identity contract. This page imports ONLY identityContract:
 * never supabase, never the library API directly. Every op proxies through the
 * capture backend, which derives the acting admin from the verified session.
 *
 * The non-admin guard here is convenience; the REAL enforcement is library's
 * actor-admin gate (a non-admin who reaches the proxy gets 403 regardless).
 */
export default function UsersSettings() {
  const [role, setRole] = useState(undefined); // undefined = loading whoAmI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [members, setMembers] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [departmentPermissions, setDepartmentPermissions] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [roleUpdates, setRoleUpdates] = useState({}); // userId -> 'saving'|'success'|<error msg>
  const [grantUpdates, setGrantUpdates] = useState({}); // `${userId}:${deptKey}` -> status
  const [expandedMember, setExpandedMember] = useState(null);
  const [effectiveAccess, setEffectiveAccess] = useState({}); // userId -> payload

  // invites
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteExpires, setInviteExpires] = useState(7);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [lastInviteUrl, setLastInviteUrl] = useState(null);

  const loadInvites = useCallback(async () => {
    const inv = await identity.listInvites();
    if (inv.ok) setInvites(inv.data?.invites || []);
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    const list = await identity.listUsers();
    if (!list.ok) {
      setError(list.error.message);
      setLoading(false);
      return;
    }
    const d = list.data || {};
    setMembers(d.members || []);
    setRoleOptions(d.roleOptions || []);
    setDepartmentPermissions(d.departmentPermissions || []);
    setDepartments(d.departments || []);
    await loadInvites();
    setLoading(false);
  }, [loadInvites]);

  useEffect(() => {
    let active = true;
    (async () => {
      const who = await identity.whoAmI();
      const r = who.ok ? who.data.role : null;
      if (!active) return;
      setRole(r);
      if (r === 'admin') await loadAll();
      else setLoading(false);
    })();
    return () => { active = false; };
  }, [loadAll]);

  const handleRoleChange = async (userId, newRole) => {
    setRoleUpdates((p) => ({ ...p, [userId]: 'saving' }));
    const r = await identity.changeUserRole(userId, newRole);
    if (r.ok) {
      setMembers((ms) => ms.map((m) => (m.user_id === userId ? { ...m, role: r.data?.member?.role || newRole } : m)));
      setRoleUpdates((p) => ({ ...p, [userId]: 'success' }));
      setTimeout(() => setRoleUpdates((p) => { const n = { ...p }; if (n[userId] === 'success') delete n[userId]; return n; }), 1500);
    } else {
      setRoleUpdates((p) => ({ ...p, [userId]: r.error.message || 'error' }));
    }
  };

  const loadEffective = async (userId) => {
    const r = await identity.getEffectiveAccess(userId);
    if (r.ok) setEffectiveAccess((p) => ({ ...p, [userId]: r.data }));
  };

  const toggleAccessEditor = async (userId) => {
    if (expandedMember === userId) { setExpandedMember(null); return; }
    setExpandedMember(userId);
    if (!effectiveAccess[userId]) await loadEffective(userId);
  };

  const handleGrantToggle = async (userId, departmentKey, permission) => {
    const member = members.find((m) => m.user_id === userId);
    const current = (member?.department_grants || {})[departmentKey] || [];
    const has = current.includes(permission);
    const nextSet = has ? current.filter((p) => p !== permission) : [...current, permission];
    const key = `${userId}:${departmentKey}`;
    setGrantUpdates((p) => ({ ...p, [key]: 'saving' }));
    // PUT-set the FULL desired permission set for this user×department (idempotent).
    const r = await identity.grantDepartment(userId, departmentKey, nextSet);
    if (r.ok) {
      setMembers((ms) => ms.map((m) => {
        if (m.user_id !== userId) return m;
        const grants = { ...(m.department_grants || {}) };
        if (nextSet.length) grants[departmentKey] = nextSet;
        else delete grants[departmentKey];
        return { ...m, department_grants: grants };
      }));
      setGrantUpdates((p) => ({ ...p, [key]: 'success' }));
      setTimeout(() => setGrantUpdates((p) => { const n = { ...p }; if (n[key] === 'success') delete n[key]; return n; }), 1200);
      if (expandedMember === userId) loadEffective(userId);
    } else {
      setGrantUpdates((p) => ({ ...p, [key]: 'error' }));
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteBusy(true);
    setInviteError(null);
    setLastInviteUrl(null);
    const r = await identity.inviteUser({ email: inviteEmail.trim(), role: inviteRole, expiresInDays: Number(inviteExpires) });
    setInviteBusy(false);
    if (r.ok) {
      setLastInviteUrl(r.data?.inviteUrl || null);
      setInviteEmail('');
      await loadInvites();
    } else {
      setInviteError(r.error.message);
    }
  };

  const handleRevoke = async (inviteId) => {
    const r = await identity.revokeInvite(inviteId);
    if (r.ok) await loadInvites();
  };

  // ── guards ──
  if (role === undefined || (role === 'admin' && loading)) {
    return <div className="usr-page"><div className="usr-loading">Loading users…</div></div>;
  }
  if (role !== 'admin') {
    return (
      <div className="usr-page">
        <div className="usr-header"><h2>Users</h2></div>
        <div className="usr-banner usr-banner-info">This page is available to tenant administrators only.</div>
      </div>
    );
  }

  const deptGrantCount = (m) => Object.keys(m.department_grants || {}).length;
  const roleChoices = roleOptions.length ? roleOptions : ['admin', 'member', 'power_user'];

  return (
    <div className="usr-page">
      <div className="usr-header">
        <h2>Users</h2>
        <p className="usr-subtitle">Manage members, roles, department access, and invitations.</p>
      </div>

      {error && <div className="usr-banner usr-banner-error">{error}</div>}

      {/* Members */}
      <section className="usr-card">
        <h3>Members ({members.length})</h3>
        <div className="usr-members">
          {members.map((m) => {
            const status = roleUpdates[m.user_id];
            const isExpanded = expandedMember === m.user_id;
            return (
              <div className="usr-member" key={m.user_id}>
                <div className="usr-member-row">
                  <div className="usr-member-id">
                    <span className="usr-name">{m.display_name || 'Unnamed user'}</span>
                    <span className="usr-uid">{m.user_id}</span>
                  </div>
                  <div className="usr-member-controls">
                    <label className="usr-role">
                      Role
                      <select
                        value={m.role}
                        onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                        disabled={status === 'saving'}
                      >
                        {roleChoices.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </label>
                    {status === 'saving' && <span className="usr-status">saving…</span>}
                    {status === 'success' && <span className="usr-status usr-ok">saved</span>}
                    {status && status !== 'saving' && status !== 'success' && <span className="usr-status usr-err">{status}</span>}
                    <span className="usr-grant-summary">
                      {deptGrantCount(m) > 0 ? `${deptGrantCount(m)} department${deptGrantCount(m) > 1 ? 's' : ''}` : 'No department grants'}
                    </span>
                    <button type="button" className="usr-btn-link" onClick={() => toggleAccessEditor(m.user_id)}>
                      {isExpanded ? 'Hide access' : 'Manage access'}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="usr-access">
                    {effectiveAccess[m.user_id]?.summary && (
                      <p className="usr-eff-summary">{effectiveAccess[m.user_id].summary}</p>
                    )}
                    <table className="usr-grant-grid">
                      <thead>
                        <tr>
                          <th>Department</th>
                          {departmentPermissions.map((p) => <th key={p}>{p}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {departments.map((dept) => {
                          const granted = (m.department_grants || {})[dept.key] || [];
                          const updKey = `${m.user_id}:${dept.key}`;
                          const gStatus = grantUpdates[updKey];
                          return (
                            <tr key={dept.key}>
                              <td>
                                {dept.label || dept.key}
                                {gStatus === 'saving' && <span className="usr-status"> saving…</span>}
                                {gStatus === 'error' && <span className="usr-status usr-err"> error</span>}
                              </td>
                              {departmentPermissions.map((p) => (
                                <td key={p} className="usr-cell">
                                  <input
                                    type="checkbox"
                                    checked={granted.includes(p)}
                                    disabled={gStatus === 'saving'}
                                    onChange={() => handleGrantToggle(m.user_id, dept.key, p)}
                                    aria-label={`${dept.key} ${p}`}
                                  />
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Invite */}
      <section className="usr-card">
        <h3>Invite a user</h3>
        <form className="usr-invite-form" onSubmit={handleInvite}>
          <input
            type="email"
            placeholder="email@example.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
          />
          <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
            <option value="member">member</option>
            <option value="admin">admin</option>
          </select>
          <select value={inviteExpires} onChange={(e) => setInviteExpires(e.target.value)}>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
          <button type="submit" className="usr-btn-primary" disabled={inviteBusy}>
            {inviteBusy ? 'Sending…' : 'Send invite'}
          </button>
        </form>
        {inviteError && <div className="usr-banner usr-banner-error">{inviteError}</div>}
        {lastInviteUrl && (
          <div className="usr-banner usr-banner-success">
            Invite created. Link:&nbsp;
            <code className="usr-invite-url">{lastInviteUrl}</code>
          </div>
        )}

        {invites.length > 0 && (
          <div className="usr-invites">
            <h4>Pending invites</h4>
            {invites.map((inv) => (
              <div className="usr-invite-row" key={inv.id}>
                <span className="usr-invite-email">{inv.email}</span>
                <span className="usr-invite-meta">{inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                <button type="button" className="usr-btn-link usr-btn-danger" onClick={() => handleRevoke(inv.id)}>Revoke</button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
